import React, { useCallback, useEffect, useState } from 'react'
import {
    StyledCloseButtonContainer,
    StyledRowContainer,
    StyledTextLink,
    StyledContainer,
    StyledIconCross,
    StyledTextContainer,
    StyledInnerRowContainer,
} from './TNStickyAccountBanner.styled'
import {
    AppState,
    AuthenticationState,
    retrieveCookie,
    storeCookie,
} from '@news-mono/web-common'
import {
    CREATE_ACCOUNT_COOKIE_NAME,
    CreateAccountBreachModalCookie,
    VERIFY_EMAIL_COOKIE_NAME,
    emailSentWithErrorNotification,
    emailSentWithSuccessNotification,
    getCookieExpiresInDay,
    storeCreateAccountModalCookie,
} from '../helpers/CreateAccountHelper'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useAuthActions } from '../../authentication'
import { useSendVerifyEmail } from '../../../user-setting/hooks/useSendVerifyEmail'
import { QueryClient, QueryClientProvider } from 'react-query'

export interface TNStickyAccountBannerProps {
    isHidden: boolean
}
const queryClient = new QueryClient()

const defaultBannerText = 'Verify your email address.'

const excludedPages = {
    'create-account': ['account-deleted'],
    'verify-email': ['email-verified', 'manage-account', 'register-success'],
}

export const TNStickyAccountBannerWrapper = (
    props: TNStickyAccountBannerProps,
) => {
    return (
        <QueryClientProvider client={queryClient}>
            <TNStickyAccountBanner {...props} />
        </QueryClientProvider>
    )
}

export const TNStickyAccountBanner: React.FC<TNStickyAccountBannerProps> = (
    props,
) => {
    const [isDisplayed, setIsDisplayed] = useState<boolean>(false)
    const [bannerText, setBannerText] = useState<string>(defaultBannerText)

    const {
        isLoggedIn,
        emailVerified,
        wanUserId: auth0Id,
        isLoading,
    } = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )
    const location = useLocation()

    const showInThisPage = useCallback(() => {
        return !(
            !isLoggedIn
                ? excludedPages['create-account']
                : excludedPages['verify-email']
        ).some((page) => location.pathname.includes(page))
    }, [location.pathname, isLoggedIn])

    const { onRegisterClick } = useAuthActions()
    const { mutateAsync: sendEmail } = useSendVerifyEmail(auth0Id)

    const onVerifyEmailClick = useCallback(async () => {
        const response = await sendEmail()
        if (response) {
            setBannerText(emailSentWithSuccessNotification)
        } else {
            setBannerText(emailSentWithErrorNotification)
        }
    }, [sendEmail])

    const handleCreateAccountPopupClose = () => {
        setIsDisplayed(false)

        const cookie = retrieveCookie<CreateAccountBreachModalCookie>(
            CREATE_ACCOUNT_COOKIE_NAME,
        )

        if (cookie) {
            storeCreateAccountModalCookie(true, new Date(cookie.expiresAt))
        }
    }

    const handleVerifyEmailPopupClose = () => {
        setIsDisplayed(false)

        const expires = getCookieExpiresInDay(1)
        storeCookie(VERIFY_EMAIL_COOKIE_NAME, expires, {
            expires: expires,
        })
    }

    useEffect(() => {
        // It cannot be shown on this page, regardless of state so set displayed to false.
        if (!showInThisPage()) {
            setIsDisplayed(false)
            return
        }

        if (!isLoggedIn) {
            const cookie = retrieveCookie<CreateAccountBreachModalCookie>(
                CREATE_ACCOUNT_COOKIE_NAME,
            )
            // hide banner if cookie is NOT found OR and stickyBannerIsDismissed is true
            if (!cookie || cookie.isStickyBannerIgnored) {
                setIsDisplayed(false)
            } else {
                setIsDisplayed(true)
            }
        } else if (!emailVerified) {
            const cookie = retrieveCookie<Date>(VERIFY_EMAIL_COOKIE_NAME)

            if (cookie === undefined) {
                setBannerText(defaultBannerText)

                setIsDisplayed(true)
            } else {
                setIsDisplayed(false)
            }
        } else {
            // hide banner if the user is logged in and email is verified
            setIsDisplayed(false)
        }
    }, [isLoggedIn, emailVerified, location, showInThisPage])

    const renditionType =
        useSelector((state: AppState) => state.render.renditionType) === 'app'
            ? 'app'
            : 'web'

    return isDisplayed && !isLoading ? (
        !isLoggedIn ? (
            <TNStickyAccountInnerBanner
                isHidden={props.isHidden}
                message="Join The Nightly for full access."
                linkText="Create free account."
                onClose={handleCreateAccountPopupClose}
                onLinkClick={() =>
                    onRegisterClick('header-banner', renditionType)
                }
            />
        ) : (
            <TNStickyAccountInnerBanner
                isHidden={props.isHidden}
                message={bannerText}
                linkText={
                    bannerText === emailSentWithSuccessNotification
                        ? undefined
                        : 'Resend verification email.'
                }
                onClose={handleVerifyEmailPopupClose}
                onLinkClick={onVerifyEmailClick}
            />
        )
    ) : null
}

interface TNStickyAccountInnerBannerProps extends TNStickyAccountBannerProps {
    message: string
    linkText?: string
    onClose: () => void
    onLinkClick: () => void
}

const TNStickyAccountInnerBanner: React.FC<TNStickyAccountInnerBannerProps> = (
    props,
) => {
    return (
        <StyledContainer isHidden={props.isHidden}>
            <StyledRowContainer>
                <StyledInnerRowContainer>
                    <StyledCloseButtonContainer onClick={props.onClose}>
                        <StyledIconCross />
                    </StyledCloseButtonContainer>
                </StyledInnerRowContainer>
                <StyledInnerRowContainer>
                    <StyledTextContainer>{props.message}</StyledTextContainer>
                    {props.linkText && (
                        <StyledTextLink onClick={props.onLinkClick}>
                            {props.linkText}
                        </StyledTextLink>
                    )}
                </StyledInnerRowContainer>
            </StyledRowContainer>
        </StyledContainer>
    )
}
